"use client";

import { useState, useEffect, useCallback } from "react";
import { 
  FiShoppingCart, FiX, FiUser, FiLogOut, FiTrash2, FiEdit2, FiPlus,
  FiMinus, FiFileText, FiHeart, FiCamera, FiMenu,
  FiCalendar,
  FiLayout,
  FiImage,
  FiChevronRight
} from "react-icons/fi";
import { supabase } from "../../Supabase/SupabaseClient";
import "./Nav.css";
// Usamos la URL pública desde la configuración central
import { IMAGES } from '../../config/supabaseImages';
import { useNavigate } from 'react-router-dom';
// import Adiciones from '../../Pages/Clients/Adiciones'

const navAdmin = [
  { href: '/Dashboard', label: 'Dashboard', icon: FiLayout },
  { href: '/contratos', label: 'Contratos', icon: FiFileText },
  { href: "/reservas", label: "Reservas", icon: FiCalendar },
  { href: '/admin-sesiones', label: 'Sesiones', icon: FiEdit2 },
  { href: '/inspiracion-admin', label: 'Subir Inspiracion', icon: FiImage },
];

const navLinks = [
  { href: "/inspiracion", label: "Inspiracion" },
  { href: "/", label: "Sobre Sole", sectionId: "sobre-sole" },
  { href: "/", label: "Como trabajamos", sectionId: "trabajo" },
  { href: "/faq", label: "Preguntas frecuentes" },
];
const userNavLinks = [
  { href: "/sesiones", label: "Mis sesiones", icon: FiCamera },
  { href: "/favoritos", label: "Favoritos", icon: FiHeart },
  { href: "/contrato", label: "Contratos", icon: FiFileText },
  { href: "/detalle-reserva", label: "Reserva", icon: FiCalendar },
];

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // Estado para el rol
  const [roleLoading, setRoleLoading] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [isProcessing] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRedirigirCompra = () => {
    setShowCartModal(false);
    navigate('/carrito');
    // fuerza recarga después de la navegación SPA
    window.location.reload();
  };

  // Función para alternar el menú móvil
  const toggleMobileMenu = () => {
    setIsOpen(!isOpen); // Cambia entre true y false
  };

  // Escuchar cambios en la sesión con Supabase
  useEffect(() => {
    // Cargar sesión existente al montar (fix para OAuth con hash)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        setRoleLoading(false);
      }
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session && session.user) {
          setUser(session.user);
        } else {
          setUser(null);
          setRole(null);
          setRoleLoading(false);
        }
      }
    );
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  // Consultar el rol en la tabla "usuarios" cada vez que cambia el usuario
  useEffect(() => {
    if (user) {
      setRoleLoading(true);
      supabase
        .from("usuarios")
        .select("rol")
        .eq("id", user.id)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error("Error al obtener el rol del usuario:", error.message);
          } else if (data) {
            setRole(data.rol);
          }
          setRoleLoading(false);
        });
    } else {
      setRole(null);
      setRoleLoading(false);
    }
  }, [user]);

  // Definir los enlaces de navegación según el rol.
  // Se hace la comparación en minúsculas para mayor robustez.
  const navigationItems = roleLoading
    ? []
    : role && String(role).toLowerCase() === "admin"
    ? navAdmin
    : userNavLinks;

  const isAdmin = roleLoading
    ? false
    : !!(role && String(role).toLowerCase() === "admin");

  // Cerrar el dropdown de usuario si se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLogoutConfirm) return;
      const userDropdown = document.querySelector('.user-dropdowns');
      const userInfo = document.querySelector('.user-info');
      if (
        userDropdown && !userDropdown.contains(event.target) &&
        userInfo && !userInfo.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLogoutConfirm]);

  // Cerrar el panel de usuario con la tecla Escape
  useEffect(() => {
    if (!showUserDropdown) return;
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowUserDropdown(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showUserDropdown]);

  // Cerrar el menú móvil al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      const mobileMenu = document.querySelector('.menu-dropdowns');
      const menuIcon = document.querySelector('.mobile-menu-icon');
      
      if (
        isOpen && 
        mobileMenu && !mobileMenu.contains(event.target) &&
        menuIcon && !menuIcon.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const confirmLogout = () => {
    setShowLogoutConfirm(true);
    setShowUserDropdown(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleLogout = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error al cerrar sesión:", error);
        setError("No se pudo cerrar la sesión.");
      } else {
        setUser(null);
        setRole(null);
      }
      setShowLogoutConfirm(false);
      window.location.href = "/";
    } catch (error) {
      console.error("Error general al cerrar sesión:", error);
      setError("No se pudo cerrar la sesión completamente. Por favor, recarga la página.");
    }
  };

  const fetchCartItems = useCallback(async () => {
    if (!user) {
      setCartItems([]);
      return;
    }
    try {
      // Obtenemos los items del carrito
      const { data: carritoItems, error: carritoError } = await supabase
        .from('carrito')
        .select('*')
        .eq("usuario_id", user.id);
  
      if (carritoError) {
        console.error("Error al obtener el carrito:", carritoError.message);
        return;
      }
  
      // Array para almacenar los items completos
      const formattedItems = [];

      // Traemos todos los servicios del carrito en una sola consulta
      const servicioIds = Array.from(
        new Set((carritoItems || []).map((i) => i.servicio_id).filter((id) => id != null))
      );

      let servicios = [];
      if (servicioIds.length > 0) {
        const { data: serviciosData, error: serviciosError } = await supabase
          .from('servicios')
          .select('id, nombre, precio, descripcion')
          .in('id', servicioIds);

        if (serviciosError) {
          console.error("Error al obtener los servicios del carrito:", serviciosError.message);
        } else {
          servicios = serviciosData || [];
        }
      }

      const serviciosMap = Object.fromEntries(servicios.map((s) => [s.id, s]));

      // Para cada item en el carrito, procesamos servicio, paquete y adicional (si existen)
      for (const item of carritoItems) {
        // Objeto para almacenar la información completa del ítem
        const formattedItem = {
          id: item.id,
          quantity: item.cantidad,
          servicio: null,
          paquete: null,
          adicional: null
        };

        // Procesamos el servicio (flujo nuevo)
        if (item.servicio_id != null) {
          formattedItem.servicio = serviciosMap[item.servicio_id] || null;
        }

        // Procesamos el paquete si existe (flujo legacy)
        if (item.paquete_id) {
          const { data: paqueteData, error: paqueteError } = await supabase
            .from('paquetes')
            .select('nombre, precio, descripcion')
            .eq('id', item.paquete_id)
            .single();
  
          if (!paqueteError && paqueteData) {
            formattedItem.paquete = paqueteData;
          }
        }
        
        // Procesamos el adicional si existe
        if (item.adicional_id) {
          const { data: adicionalData, error: adicionalError } = await supabase
            .from('adicional')
            .select('nombre, precio, descripcion')
            .eq('id', item.adicional_id)
            .single();
  
          if (!adicionalError && adicionalData) {
            formattedItem.adicional = adicionalData;
          }
        }
        
        // Agregamos el ítem completo a la lista
        formattedItems.push(formattedItem);
      }
  
      setCartItems(formattedItems);
      
      // Calculamos el total sumando servicios, paquetes y adicionales
      const total = formattedItems.reduce((sum, item) => {
        let itemTotal = 0;
        if (item.servicio) {
          itemTotal += (Number(item.servicio.precio) || 0) * item.quantity;
        }
        if (item.paquete) {
          itemTotal += item.paquete.precio * item.quantity;
        }
        if (item.adicional) {
          itemTotal += item.adicional.precio * item.quantity;
        }
        return sum + itemTotal;
      }, 0);
      
      setTotalAmount(total);
    } catch (error) {
      console.error("Error al procesar el carrito:", error);
      setCartItems([]);
    }
  }, [user]);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  useEffect(() => {
    const handleCartUpdate = (event) => {
      fetchCartItems();
      if (event.detail && event.detail.showModal) {
        setShowCartModal(true);
      }
    };
    document.addEventListener('cartUpdate', handleCartUpdate);
    return () => document.removeEventListener('cartUpdate', handleCartUpdate);
  }, [fetchCartItems]);

  const updateItemQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      const { error } = await supabase
        .from("carrito")
        .update({ cantidad: newQuantity })
        .eq("id", itemId);
      if (error) {
        console.error("Error al actualizar la cantidad:", error);
        return;
      }
      
      setCartItems(prevItems =>
        prevItems.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item)
      );
      
      // Recalculamos el total
      const updatedItems = cartItems.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item);
      const updatedTotal = updatedItems.reduce((sum, item) => {
        let itemTotal = 0;
        if (item.servicio) {
          itemTotal += (Number(item.servicio.precio) || 0) * item.quantity;
        }
        if (item.paquete) {
          itemTotal += item.paquete.precio * item.quantity;
        }
        if (item.adicional) {
          itemTotal += item.adicional.precio * item.quantity;
        }
        return sum + itemTotal;
      }, 0);
      
      setTotalAmount(updatedTotal);
    } catch (error) {
      console.error("Error al actualizar cantidad:", error);
    }
  };

  const removeCartItem = async (itemId) => {
    try {
      const { error } = await supabase
        .from("carrito")
        .delete()
        .eq("id", itemId);

      if (error) {
        console.error("Error al eliminar el ítem:", error);
        return;
      }

      const updatedItems = cartItems.filter(item => item.id !== itemId);
      setCartItems(updatedItems);

      // Recalculamos el total
      const updatedTotal = updatedItems.reduce((sum, item) => {
        let itemTotal = 0;
        if (item.servicio) {
          itemTotal += (Number(item.servicio.precio) || 0) * item.quantity;
        }
        if (item.paquete) {
          itemTotal += item.paquete.precio * item.quantity;
        }
        if (item.adicional) {
          itemTotal += item.adicional.precio * item.quantity;
        }
        return sum + itemTotal;
      }, 0);

      setTotalAmount(updatedTotal);
    } catch (error) {
      console.error("Error al eliminar el ítem:", error);
    }
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const navigateTo = (href) => {
    window.location.href = href;
    setShowUserDropdown(false);
  };

  // Función para calcular el subtotal de un ítem individual
  const calculateItemSubtotal = (item) => {
    let subtotal = 0;
    if (item.servicio) {
      subtotal += (Number(item.servicio.precio) || 0) * item.quantity;
    }
    if (item.paquete) {
      subtotal += item.paquete.precio * item.quantity;
    }
    if (item.adicional) {
      subtotal += item.adicional.precio * item.quantity;
    }
    return subtotal;
  };

  // LOGO: usamos la URL pública y fallback inline si no está disponible
  const logoSrc = IMAGES?.logos?.logoNegro || IMAGES?.logos?.logoBlanco || '';
  const PLACEHOLDER_LOGO =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60" viewBox="0 0 200 60"><rect width="100%" height="100%" fill="#fff"/><text x="50%" y="50%" fill="#9ca3af" font-family="Arial, sans-serif" font-size="14" dominant-baseline="middle" text-anchor="middle">Logo</text></svg>`
    );

  const handleLogoError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = PLACEHOLDER_LOGO;
    e.currentTarget.alt = 'Logo (fallback)';
  };

  // Avatar fallback: si el usuario no tiene avatar, usamos el logo o placeholder
  const avatarFallback = IMAGES?.logos?.logoNegroSintesis || logoSrc || PLACEHOLDER_LOGO;
  const handleAvatarError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = avatarFallback;
    e.currentTarget.alt = 'Avatar (fallback)';
  };

  const displayName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email ||
    'Usuario';

  return (
    <nav className="nav">
      <div className="nav-container">
        {error && <div className="error-notification">{error}</div>}
        <div className="nav-content">
          <div className="nav-logo">
            <a href="/" className="logo-link" aria-label="Inicio">
              <img
                src={logoSrc || PLACEHOLDER_LOGO}
                alt="Logo"
                className="logo"
                onError={handleLogoError}
                loading="lazy"
                width="180"
                height="60"
              />
            </a>
          </div>

          {/* Ícono de menú mobile visible solo en tablets y móviles */}
          <div className='mobile-menu-container'>
            <FiMenu className="mobile-menu-icon" onClick={toggleMobileMenu} aria-label="Abrir menú móvil" />
          </div>

          {/* Menú de navegación general: muestra navAdmin si rol es Admin, de lo contrario navLinks */}
          <div className={`menu-dropdowns ${isOpen ? "open" : ""}`}>
            <div className="nav-links">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.sectionId ? `/#${link.sectionId}` : link.href}
                  className="nav-link"
                  onClick={(e) => {
                    setIsOpen(false);
                    if (link.sectionId) {
                      e.preventDefault();
                      const goToSection = () => {
                        const el = document.getElementById(link.sectionId);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      };
                      if (window.location.pathname !== "/") {
                        navigate("/");
                        setTimeout(goToSection, 150);
                      } else {
                        goToSection();
                      }
                    }
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          
          <div className="nav-cart">
            <button className="cart-button" onClick={() => setShowCartModal(true)} aria-label="Abrir carrito">
              <FiShoppingCart className="icon" />
              {cartItems.length > 0 && (
                <span className="cart-count" aria-live="polite">{cartItems.length}</span>
              )}
            </button>

            {user ? (
              <div className="user-info">
                <button
                  type="button"
                  className="user-avatar-trigger"
                  onClick={toggleUserDropdown}
                  aria-haspopup="menu"
                  aria-expanded={showUserDropdown}
                  aria-label="Abrir menú de usuario"
                >
                  <img
                    src={user.user_metadata?.avatar_url || avatarFallback}
                    alt=""
                    className="user-avatar"
                    onError={handleAvatarError}
                    width="40"
                    height="40"
                  />
                  <span className="user-avatar-caret" aria-hidden="true" />
                </button>
                {showUserDropdown && (
                  <div className="user-dropdowns" role="menu">
                    <div className="user-dropdown-header">
                      <img
                        src={user.user_metadata?.avatar_url || avatarFallback}
                        alt=""
                        className="dropdown-user-avatar"
                        onError={handleAvatarError}
                        width="52"
                        height="52"
                      />
                      <div className="dropdown-user-info">
                        <p className="dropdown-user-names" title={displayName}>
                          {displayName}
                        </p>
                        <p className="dropdown-user-email" title={user.email}>
                          {user.email}
                        </p>
                      </div>
                    </div>

                    <p className="user-dropdown-section-title">
                      {isAdmin ? 'Panel de administración' : 'Mi cuenta'}
                    </p>

                    {/* Usamos navigationItems para el dropdown de usuario */}
                    <div className="user-dropdown-links">
                      {roleLoading ? (
                        <p className="user-dropdown-loading">Cargando tu cuenta...</p>
                      ) : (
                        navigationItems.map((link) => (
                          <button
                            key={link.href}
                            type="button"
                            role="menuitem"
                            className="user-dropdown-link"
                            onClick={() => navigateTo(link.href)}
                          >
                            <span className="dropdown-link-icon-wrap" aria-hidden="true">
                              <link.icon className="dropdown-link-icon" />
                            </span>
                            <span className="dropdown-link-label">{link.label}</span>
                            <FiChevronRight className="dropdown-link-arrow" aria-hidden="true" />
                          </button>
                        ))
                      )}
                    </div>

                    <div className="user-dropdown-footer">
                      <button
                        type="button"
                        role="menuitem"
                        className="dropdown-logout-button"
                        onClick={confirmLogout}
                      >
                        <span className="dropdown-link-icon-wrap" aria-hidden="true">
                          <FiLogOut className="dropdown-link-icon" />
                        </span>
                        <span className="dropdown-link-label">Cerrar sesión</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button className="google-login-button" onClick={handleLogin} aria-label="Iniciar sesión">
                <FiUser className="icon" /> Iniciar sesión
              </button>
            )}
          </div>
        </div>

        {/* Modal de confirmación para cerrar sesión */}
        {showLogoutConfirm && (
          <div className="logout-confirm-overlay" role="dialog" aria-modal="true">
            <div className="logout-confirm-modal">
              <h3>Cerrar sesión</h3>
              <p>¿Estás seguro que deseas cerrar sesión?</p>
              <div className="logout-confirm-buttons">
                <button className="logout-cancel-button" onClick={cancelLogout}>
                  Cancelar
                </button>
                <button className="logout-confirm-button" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal del carrito de compras */}
        {showCartModal && (
          <div className="cart-modal-overlay" role="dialog" aria-modal="true">
            <div className="cart-modal">
              <div className="cart-modal-header">
                <h3>Tu Carrito de Compra</h3>
                <button className="cart-close-button" onClick={() => setShowCartModal(false)} aria-label="Cerrar carrito">
                  <FiX className="icon" />
                </button>
              </div>
              <div className="cart-modal-content">
                {cartItems.length === 0 ? (
                  <div className="empty-cart-message">
                    <p>Tu carrito está vacío</p>
                    {/* <Adiciones/> */}
                  </div>
                ) : (
                  <>
                    <div className="cart-items-list">
                      {cartItems.map(item => (
                        <div key={item.id} className="cart-item">
                          <div className="cart-item-info">
                            {/* Mostramos información del servicio si existe */}
                            {item.servicio && (
                              <div className="cart-package-info">
                                <h4 className="cart-item-name">{item.servicio.nombre}</h4>
                                {item.servicio.descripcion && (
                                  <p className="cart-item-desc">{item.servicio.descripcion}</p>
                                )}
                                {item.servicio.precio != null && (
                                  <p className="cart-item-price">
                                    ${Number(item.servicio.precio).toFixed(2)}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Mostramos información del paquete si existe */}
                            {item.paquete && (
                              <div className="cart-package-info">
                                <h4 className="cart-item-name">{item.paquete.nombre}</h4>
                                <p className="cart-item-desc">{item.paquete.descripcion}</p>
                                <p className="cart-item-price">${item.paquete.precio.toFixed(2)}</p>
                              </div>
                            )}
                            
                            {/* Mostramos información del adicional si existe */}
                            {item.adicional && (
                              <div className="cart-addon-info">
                                <h5 className="cart-addon-name">
                                  {item.adicional.nombre}
                                </h5>
                                <p className="cart-addon-desc">{item.adicional.descripcion}</p>
                                <p className="cart-addon-price">
                                  ${item.adicional.precio.toFixed(2)}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="cart-item-actions">
                            <div className="quantity-controls">
                              <button 
                                className="quantity-button"
                                onClick={() => updateItemQuantity(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                aria-label="Disminuir cantidad"
                              >
                                <FiMinus className="icon" />
                              </button>
                              <span className="quantity-value" aria-live="polite">{item.quantity}</span>
                              <button 
                                className="quantity-button"
                                onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                                aria-label="Aumentar cantidad"
                              >
                                <FiPlus className="icon" />
                              </button>
                            </div>
                            <button 
                              className="remove-item-button"
                              onClick={() => removeCartItem(item.id)}
                              aria-label="Eliminar ítem"
                            >
                              <FiTrash2 className="icon" />
                            </button>
                          </div>
                          <div className="cart-item-subtotal">
                            <p>Subtotal: ${calculateItemSubtotal(item).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="cart-summary" role="status" aria-live="polite">
                      <div className="cart-total">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">${totalAmount.toFixed(2)}</span>
                      </div>
                      <button 
                        className="checkout-button"
                        onClick={handleRedirigirCompra}
                        disabled={isProcessing || cartItems.length === 0}
                      >
                        Proceder al Pago
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;